import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon, Storage } from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdLabeledValue from '@/components/ui/CpdLabeledValue';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import { useGetSftpConnection } from '@/api/hooks/useGetSftpConnection';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';
import useFormValidation from '@/hooks/useFormValidation';
import { useConfirmation } from '@/context/ConfirmationContext';
import SftpConnectionBasicInfoSection from './SftpConnections/SftpConnectionBasicInfoSection';
import { formatTimestamp } from '@/utils/date/date';

const SftpConnectionDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: organizationId, connectionId } = useParams();
  const { requestConfirmation } = useConfirmation();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const {
    data: connection,
    loading: fetchLoading,
    error: fetchError,
    refetch,
  } = useGetSftpConnection(organizationId, connectionId);

  const handleBack = () => {
    navigate(`/admin/updateOrganization/${organizationId}#sftp-connections`);
  };

  const handleSave = async () => {
    clearAllErrors();
    const formData = formRef.current?.formData;
    if (!formData) return;

    setLoading(true);
    try {
      await SftpConnectionsApi.updateSftpConnection(
        organizationId,
        connectionId,
        formData,
      );
      toast.success(t('sftpConnections.updateSuccess'));
      refetch();
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = () => {
    requestConfirmation(
      t('sftpConnections.confirmDelete', {
        name: connection?.alias_name,
      }),
      async () => {
        setLoading(true);
        try {
          await SftpConnectionsApi.deleteSftpConnection(
            organizationId,
            connectionId,
          );
          toast.success(t('sftpConnections.deleteSuccess'));
          handleBack();
        } catch (err) {
          toast.error(
            err.response?.data?.message || 'Failed to delete connection',
          );
        } finally {
          setLoading(false);
        }
      },
    );
  };

  if (fetchLoading) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (fetchError) {
    return (
      <PageLayout>
        <Typography color='error'>{fetchError}</Typography>
      </PageLayout>
    );
  }

  const connectionData = connection;

  return (
    <PageLayout
      overflow='auto'
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBack sx={{ color: 'white' }} />
            </IconButton>
            <Storage sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              {connectionData?.alias_name || t('sftpConnections.details.title')}
            </Typography>
          </Box>
        </CardHeader>
      }>
      <FormErrorSummary errors={generalErrors} />

      <Grid container spacing={3}>
        {/* Metadata Section */}
        <Grid item xs={12}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            {t('sftpConnections.details.metadata')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CpdLabeledValue
                label={t('sftpConnections.table.createdAt')}
                value={formatTimestamp(connectionData?.created_at)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CpdLabeledValue
                label={t('sftpConnections.table.createdBy')}
                value={connectionData?.created_by?.email || '-'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CpdLabeledValue
                label={t('sftpConnections.table.updatedAt')}
                value={formatTimestamp(connectionData?.updated_at)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CpdLabeledValue
                label={t('sftpConnections.table.updatedBy')}
                value={connectionData?.updated_by?.email || '-'}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <SftpConnectionBasicInfoSection
            ref={formRef}
            organizationId={organizationId}
            fieldErrors={fieldErrors}
            isEdit={true}
            initialData={connectionData}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 3,
        }}>
        <Button variant='outlined' color='error' onClick={handleDeactivate}>
          {t('common.buttons.delete')}
        </Button>

        <CpdActionButton
          onClick={handleSave}
          loading={loading}
          startIcon={<SaveIcon sx={{ color: 'white' }} />}
          disabled={loading}>
          {loading ? t('sftpConnections.saving') : t('sftpConnections.save')}
        </CpdActionButton>
      </Box>
    </PageLayout>
  );
};

export default SftpConnectionDetails;
